import { ActionReducerMapBuilder, AsyncThunk, PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import moment from 'moment';
import { AppointmentType, AutomedDoctorAvailabilityTimeModel, availabilityService } from 'src/services/availability';
import isEqual from 'lodash/isEqual';

import { AppointmentByHash, AutomedBookingRequestResponse, bookingService, BookingUserProfileModel, UmbracoClinicModel } from 'src/services/booking';
import { queryStringOrSessionStorage } from 'src/utils/queryStringOrSessionStorage';
import { AsyncState } from '..';
import { AutomedDetails, siteService } from 'src/services/site';

type FetchClinicArgs = {siteId: string | number};
type FetchDoctorArgs = {siteId: string | number, doctorId: string | number};
type FetchAppointmentTypeArgs = {siteId: string | number, appointmentTypeId: string | number};
type CancelAppointmentArgs = {siteId: string | number, appointmentId: string | number};

export type AppointmentState = {
  dateTime: string;
  timeSlot: AutomedDoctorAvailabilityTimeModel;
  doctorErpId: number;
  clinicId: number;
  appointmentTypeId: number | string;
  isNewPatient: boolean;
  reservedBooking?: AutomedBookingRequestResponse;
  clinic: AsyncState<FetchClinicArgs, UmbracoClinicModel & AutomedDetails>
  doctor: AsyncState<FetchDoctorArgs, BookingUserProfileModel>
  appointmentType: AsyncState<FetchAppointmentTypeArgs, AppointmentType>
}

let isFetchingClinic: FetchClinicArgs | undefined = undefined;
export const fetchClinic = createAsyncThunk(
  'appointment/fetchClinic',
  (async (args: FetchClinicArgs) => {
    if(args.siteId && !isEqual(isFetchingClinic, args)) {
      isFetchingClinic = args;
      const [umbracoDetails, automedDetails] = await Promise.all([
        bookingService.GetClinicUmbracoDetails(args.siteId),
        siteService.GetAutomedDetails(args.siteId)
      ]);
      return {...umbracoDetails, ...automedDetails} as UmbracoClinicModel & AutomedDetails;
    }
  })
);

let isFetchingDoctor: FetchDoctorArgs | undefined = undefined;
export const fetchDoctor = createAsyncThunk(
  'appointment/fetchDoctor',
  (async (args: FetchDoctorArgs) => {
    if(args.siteId && args.doctorId && !isEqual(isFetchingDoctor, args)) {
      isFetchingDoctor = args;
    return await bookingService.GetDoctorDetails(args.siteId, args.doctorId)
    }
  })
);

let isFetchingAppt: FetchAppointmentTypeArgs | undefined = undefined;
export const fetchAppointmentType = createAsyncThunk(
  'appointment/fetchAppointmentType',
  (async (args: FetchAppointmentTypeArgs) => {
    if(args.siteId && !isEqual(args, isFetchingAppt)) {
      isFetchingAppt = args;
      return await availabilityService.GetAppointmentTypes(args.siteId);
    }
  })
);
export const cancelAppointment = createAsyncThunk(
  'appointment/cancelAppointment',
  (async ({siteId, appointmentId}: CancelAppointmentArgs) => await bookingService.CancelBooking(siteId, appointmentId))
);

const reservedBooking = Object.keys(sessionStorage).reduce((createBookingResponse, key) => {
  const [a,b] = key.split('.')
  if(a === "reservedBooking" && b) {
    createBookingResponse[b as keyof AutomedBookingRequestResponse] = JSON.parse(sessionStorage.getItem(key)!);
  }
  return createBookingResponse
}, {} as Partial<AutomedBookingRequestResponse>);

const initialState: AppointmentState = {
  dateTime: queryStringOrSessionStorage("dateTime")!,
  timeSlot: {
    duration: queryStringOrSessionStorage("timeSlot.duration")!,
    time: queryStringOrSessionStorage("timeSlot.time")!,
    ab: queryStringOrSessionStorage("timeSlot.ab") ?? undefined,
    r1: queryStringOrSessionStorage("timeSlot.r1") ?? undefined,
    r2: queryStringOrSessionStorage("timeSlot.r2") ?? undefined,
    r3: queryStringOrSessionStorage("timeSlot.r3") ?? undefined,
    r4: queryStringOrSessionStorage("timeSlot.r4") ?? undefined,
  },
  doctorErpId: Number(queryStringOrSessionStorage("doctor")),
  clinicId: Number(queryStringOrSessionStorage("clinic")),
  appointmentTypeId: queryStringOrSessionStorage("appointment")!,
  isNewPatient: queryStringOrSessionStorage("new")! === "true",
  reservedBooking: ("appointment_id" in reservedBooking) ? reservedBooking as AutomedBookingRequestResponse : undefined,
  clinic: {isLoading: false},
  doctor: {isLoading: false},
  appointmentType: {isLoading: false},
}

function addCases<TArgs, TData, TReturn = TData>(
  builder: ActionReducerMapBuilder<AppointmentState>,
  key: keyof AppointmentState,
  request: AsyncThunk<TData, TArgs, object>,
  transform?: (payload: TData, args: TArgs) => TReturn,
  furtherFulfilled?: (state: AppointmentState) => void
) {
  builder.addCase(request.pending, (state, action) => {
    (state[key as keyof typeof state] as AsyncState<TArgs, TReturn>) = {
      isLoading: true, 
      request: action.meta.arg
    };
  });
  builder.addCase(request.fulfilled, (state, action) => {
      (state[key as keyof typeof state] as AsyncState<TArgs, TReturn>) = {
        data: transform ? transform(action.payload, action.meta.arg) : action.payload as unknown as TReturn,
        isLoading: false,
        request: action.meta.arg
      };
    furtherFulfilled?.(state);
  });
  builder.addCase(request.rejected, (state, action) => {
    (state[key as keyof typeof state] as AsyncState<TArgs, TReturn>) = {
      isLoading: false,
      error: action.error,
      request: action.meta.arg
    }
  });
}

export const appointmentSlice = createSlice({
  name: 'appointment',
  initialState: initialState,
  reducers: {
    setClinicId: (state, action: PayloadAction<number>) => {
      state.clinicId = action.payload;
    },
    changeTime: (state, action: PayloadAction<{date: Date, timeSlot: AutomedDoctorAvailabilityTimeModel}>) => {
        state.dateTime = action.payload.date.toISOString();
        sessionStorage.setItem("dateTime", action.payload.date.toISOString());
        state.timeSlot = action.payload.timeSlot;
        for(const [key,value] of Object.entries(action.payload.timeSlot)) {
          if(value) {
            sessionStorage.setItem(`timeSlot.${key}`, value);
          } else {
            sessionStorage.removeItem(`timeSlot.${key}`);
          }
        }
    },
    setReservedBooking: (state, action: PayloadAction<AutomedBookingRequestResponse>) => {
      state.reservedBooking = action.payload;
      for(const [key,value] of Object.entries(action.payload)) {
        sessionStorage.setItem(`reservedBooking.${key}`, JSON.stringify(value));
      }
    },
    removeReservedBooking: (state, _action: PayloadAction<void>) => {
      if(state.reservedBooking) {
      for(const [key,value] of Object.entries(state.reservedBooking)) {
        sessionStorage.setItem(`reservedBooking.${key}`, JSON.stringify(value));
      }
    }
      state.reservedBooking = undefined;

    },
    setAppointmentTypeId: (state, action: PayloadAction<string>) => {
      state.appointmentTypeId = action.payload;
    },
    setAppointmentFromHash: (state, action: PayloadAction<AppointmentByHash>) => {
      state.appointmentTypeId = action.payload.appointment_type_id;
      state.dateTime = moment(`${action.payload.date} ${action.payload.time}`).toISOString();
      state.doctorErpId = action.payload.user_id;
    },
    setIsNewPatient: (state, action: PayloadAction<boolean>) => {
      state.isNewPatient = action.payload;
      sessionStorage.setItem("new", action.payload.toString())
    }
  },
  extraReducers(builder) {
    addCases(builder, 'clinic', fetchClinic, undefined, () => isFetchingClinic = undefined);
    addCases(builder, 'doctor', fetchDoctor, undefined, () => isFetchingDoctor = undefined);
    addCases(builder, 'appointmentType', fetchAppointmentType, 
      (payload, args) => payload?.find(a => a.appointmentTypeId === `${args.appointmentTypeId}`),
      () => isFetchingAppt = undefined);

    builder.addCase(cancelAppointment.fulfilled, (state) => {
      if(state.reservedBooking) {
      for(const [key] of Object.entries(state.reservedBooking!)) {
        sessionStorage.removeItem(`reservedBooking.${key}`);
      }
      state.reservedBooking = undefined;
    }
    })

  },
})

// Action creators are generated for each case reducer function
export const { changeTime, setClinicId, setAppointmentFromHash, setAppointmentTypeId, removeReservedBooking } = appointmentSlice.actions

export default appointmentSlice.reducer